import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
	BankDetailsClient,
	BillingScheduleResponse,
	BillingSummaryClient,
	BillingSummaryResponse,
	BillingTermSummaryResponse,
	ChangeBankDetailsCommand,
	GetPaymentDayResponse,
	PaymentClient,
	ScheduleClient,
} from '@acorn-portal/shared/src/services/managedPaymentsApi';
import API_POLICY_TOKEN from '@acorn-portal/shared/src/services/managedPaymentsInstance';

type IStatus = 'idle' | 'loading' | 'failed';

export interface IManagedPayments {
	billingTerms: BillingSummaryResponse;
	currentBillingTerm: BillingTermSummaryResponse;
	paymentDay: GetPaymentDayResponse;
	paymentSchedule: BillingScheduleResponse;
	status: IStatus;
}

const initialState: IManagedPayments = {
	billingTerms: { billingTerms: [] },
	currentBillingTerm: {
		termNumber: 0,
		termStart: '',
		termEnd: '',
		nextCollectionDate: '',
	},
	paymentDay: {
		canChangePaymentDay: false,
	},
	paymentSchedule: { schedules: [], canChangeBankDetails: false },
	status: 'idle',
};

export const getSummary = createAsyncThunk('managedPayments/getSummary', async () => {
	try {
		const client = new BillingSummaryClient(undefined, API_POLICY_TOKEN);
		const response = await client.getSummary();

		return response;
	} catch (err) {
		return err;
	}
});

export const getSummaryForCurrentTerm = createAsyncThunk('managedPayments/getSummaryForCurrentTerm', async () => {
	try {
		const client = new BillingSummaryClient(undefined, API_POLICY_TOKEN);
		const response = await client.getSummaryForCurrentTerm();
		return response.billingTerm;
	} catch (err) {
		return err;
	}
});

export const getPaymentDate = createAsyncThunk('managedPayments/getPaymentDay', async () => {
	try {
		const client = new PaymentClient(undefined, API_POLICY_TOKEN);
		const response = await client.getPaymentDay();
		return response;
	} catch (err) {
		console.error(err);
		return err;
	}
});

export const getBillingSchedule = createAsyncThunk<BillingScheduleResponse>(
	'managedPayments/getBillingSchedule',
	async (_, { rejectWithValue }) => {
		try {
			const client = new ScheduleClient(undefined, API_POLICY_TOKEN);
			const response = await client.getBillingSchedule();
			return response;
		} catch (err) {
			console.error('Error retrieving the billing schedule', err);
			return rejectWithValue('Error retrieving the billing schedule');
		}
	},
);

export const changeBankDetails = createAsyncThunk(
	'mangedPayments/MakePayment',
	async (data: ChangeBankDetailsCommand) => {
		try {
			const client = new BankDetailsClient(undefined, API_POLICY_TOKEN);
			const response = await client.changeBankDetails(data);
			return response;
		} catch (err) {
			console.error(err);
		}
	},
);

export const managedPaymentsSlice = createSlice({
	name: 'managedPayments',
	initialState,
	reducers: {
		clearManagedPaymentsDetails: () => ({
			...initialState,
		}),
		updateManagedPaymentDetails: (state, action: PayloadAction<Partial<IManagedPayments>>) => ({
			...state,
			...action.payload,
		}),
		setCanChangePaymentDay: (state, action: PayloadAction<boolean>) => {
			state.paymentDay.canChangePaymentDay = action.payload;
		},
		setCanChangeBankDetails: (state, action: PayloadAction<boolean>) => {
			state.paymentSchedule.canChangeBankDetails = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(getSummaryForCurrentTerm.pending, (state) => {
			state.status = 'loading';
		});
		builder.addCase(getSummaryForCurrentTerm.fulfilled, (state, action) => {
			state.currentBillingTerm = action.payload as BillingTermSummaryResponse;
			state.status = 'idle';
		});
		builder.addCase(getSummaryForCurrentTerm.rejected, (state) => {
			state.status = 'failed';
		});

		builder.addCase(getPaymentDate.pending, (state) => {
			state.status = 'loading';
		});
		builder.addCase(getPaymentDate.fulfilled, (state, action) => {
			state.paymentDay = action.payload as GetPaymentDayResponse;
			state.status = 'idle';
		});
		builder.addCase(getPaymentDate.rejected, (state) => {
			state.status = 'failed';
		});

		builder.addCase(getBillingSchedule.pending, (state) => {
			state.status = 'loading';
		});
		builder.addCase(getBillingSchedule.fulfilled, (state, action) => {
			state.paymentSchedule = action.payload;
			state.status = 'idle';
		});
		builder.addCase(getBillingSchedule.rejected, (state) => {
			state.status = 'failed';
		});

		builder.addCase(getSummary.pending, (state) => {
			state.status = 'loading';
		});
		builder.addCase(getSummary.fulfilled, (state, action) => {
			state.billingTerms = action.payload as BillingSummaryResponse;
			state.status = 'idle';
		});
		builder.addCase(getSummary.rejected, (state) => {
			state.status = 'failed';
		});
	},
});

export const {
	clearManagedPaymentsDetails,
	updateManagedPaymentDetails,
	setCanChangeBankDetails,
	setCanChangePaymentDay,
} = managedPaymentsSlice.actions;
export default managedPaymentsSlice.reducer;
